import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import {
  ICertificadoEntidad,
  ICodigoDispositivo,
  ICreateEventoGenerico,
  ITracker,
  IDispositivoAlarma,
  IActivo,
  IEventoBaseGenerico,
  IEventoGenerico,
  IUpdateCertificadoEntidad,
} from 'modelos/src';
import { CommonModule } from '@angular/common';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { CertificadoEntidadService } from '../../../../auxiliares/servicios/http/certificado-entidad.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { EventosGenericosHttpService } from '../../../../auxiliares/servicios/http/eventos-genericos.service';

export interface ICrearCertificadoManualData {
  certificadoId: string;
}

interface ICodigoSeleccionable extends ICodigoDispositivo {
  seleccionado: boolean;
}

@Component({
  imports: [CommonModule, AuxiliaresModule],
  selector: 'app-crear-certificado-manual',
  templateUrl: './crear-certificado-manual.component.html',
  styleUrls: ['./crear-certificado-manual.component.scss'],
})
export class CrearCertificadoManualComponent implements OnInit, OnDestroy {
  dialogData = inject<ICrearCertificadoManualData>(MAT_DIALOG_DATA, { optional: true });
  dialogRef = inject<MatDialogRef<CrearCertificadoManualComponent>>(MatDialogRef, { optional: true });
  private certificadoService = inject(CertificadoEntidadService);
  helper = inject(HelperService);

  public loading = false;
  public form?: FormGroup;
  public title = 'Crear Certificado Manual';
  public certificado?: ICertificadoEntidad;
  public codigosDisponibles: ICodigoSeleccionable[] = [];
  public todosSeleccionados = false;
  private guardadoExitoso = false;

  async ngOnInit(): Promise<void> {
    if (!this.dialogData?.certificadoId) {
      this.helper.notifError('No se proporcionó ID de certificado');
      this.cerrar();
      return;
    }

    this.createForm();
    await this.cargarCertificado();
    await this.cargarCodigosDisponibles();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  private createForm(): void {
    this.form = new FormGroup({
      observaciones: new FormControl(''),
    });
  }

  private async cargarCertificado(): Promise<void> {
    try {
      this.loading = true;
      this.certificado = await this.certificadoService.getById(
        this.dialogData.certificadoId,
      );
      this.loading = false;
    } catch (error) {
      this.loading = false;
      this.helper.notifError('Error al cargar el certificado');
      this.cerrar();
    }
  }

  private async cargarCodigosDisponibles(): Promise<void> {
    try {
      let codigos: ICodigoDispositivo[] = [];

      if (this.certificado?.activo) {
        codigos =
          this.certificado?.activo?.tracker?.modelo?.codigos?.codigos || [];
      } else {
        const alarma = this.certificado?.alarma as IDispositivoAlarma;
        codigos = alarma?.modelo?.codigos?.codigos || [];
      }

      // Convertir a códigos seleccionables
      this.codigosDisponibles = codigos.map((codigo) => ({
        ...codigo,
        seleccionado: false,
      }));
    } catch (error) {
      this.helper.notifError('Error al cargar códigos disponibles');
      console.error('Error cargando códigos:', error);
    }
  }

  public toggleSeleccionCodigo(codigo: ICodigoSeleccionable): void {
    codigo.seleccionado = !codigo.seleccionado;
    this.actualizarEstadoTodosSeleccionados();
  }

  public toggleTodos(): void {
    this.todosSeleccionados = !this.todosSeleccionados;
    this.codigosDisponibles.forEach((codigo) => {
      codigo.seleccionado = this.todosSeleccionados;
    });
  }

  private actualizarEstadoTodosSeleccionados(): void {
    this.todosSeleccionados =
      this.codigosDisponibles.length > 0 &&
      this.codigosDisponibles.every((c) => c.seleccionado);
  }

  public getCodigosSeleccionados(): ICodigoSeleccionable[] {
    return this.codigosDisponibles.filter((c) => c.seleccionado);
  }

  public async onSubmit(): Promise<void> {
    const codigosSeleccionados = this.getCodigosSeleccionados();

    if (codigosSeleccionados.length === 0) {
      this.helper.notifWarn('Seleccioná al menos un código');
      return;
    }

    this.loading = true;
    try {
      const update: IUpdateCertificadoEntidad = {};
      update.eventosRegistrados =
        this.simularEventosCodigos(codigosSeleccionados);
      await this.certificadoService.update(this.certificado!._id!, update);
      this.guardadoExitoso = true;
      this.helper.notifSuccess('Certificado manual creado correctamente');
      this.loading = false;
      if (this.dialogRef) {
        this.dialogRef.close(true);
      }
    } catch (error) {
      this.loading = false;
      this.helper.notifError(error);
    }
  }

  private simularEventosCodigos(
    codigos: ICodigoSeleccionable[],
  ): IEventoGenerico[] {
    const eventosCreados: IEventoGenerico[] = [];
    const campoCodigo = this.certificado?.activo
      ? 'codigoTracker'
      : 'codigoAlarma';

    for (const codigo of codigos) {
      // Estructura base del evento - REVISAR Y COMPLETAR CON CAMPOS REALES
      const eventoSimulado: IEventoGenerico = {
        tipoEvento: this.certificado?.activo
          ? 'Evento Vehiculo'
          : 'Evento Alarma',
        fechaCreacion: new Date().toISOString(),
        valores: {
          [campoCodigo]: codigo.codigo,
        },
      };
      eventosCreados.push(eventoSimulado);
    }
    return eventosCreados;
  }

  public async cerrar(): Promise<void> {
    // Si no se guardó exitosamente, eliminar el certificado creado
    if (!this.guardadoExitoso && this.certificado?._id) {
      try {
        await this.certificadoService.delete(this.certificado._id);
      } catch (error) {
        console.error('Error al eliminar certificado:', error);
        // No mostramos error al usuario para no confundir al cerrar
      }
    }

    if (this.dialogRef) {
      this.dialogRef.close(false);
    }
  }

  public getEntidadInfo(): string {
    if (this.certificado?.activo) {
      return this.certificado?.activo.identificacion || 'Sin identificación';
    } else {
      return this.certificado?.alarma?.nombre || 'Sin nombre';
    }
  }
}
